import React, { useState, useRef, useEffect } from "react";
import { FaTimes, FaUpload } from "react-icons/fa";
import { SuccessToast, WarningToast } from "../../../../Util";
import { CreateLoanRequest, GetSameMonazamHGOs, GetHGODetails } from "../../../../Actions/RequestActions";
import { useRollId } from "../CommonComponents/RollIdContext";
import { Spinner } from "react-bootstrap";

const LoanRequestForm = ({ isOpen, onClose }) => {
  const { rollId } = useRollId();
  const [formData, setFormData] = useState({
    borrower_id: "",
    currency: "SAR",
    narration: "",
    amount: "",
    letter_of_acknowledgement: null,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [hgos, setHGOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [monazamId, setMonazamId] = useState("");
  const [currentHgoId, setCurrentHgoId] = useState("");
  const fileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "letter_of_acknowledgement") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: files[0] || null,
      }));
    } else if (name === "amount") {
      let cleanValue = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/(\.\d{2})\d+/, "$1");
      setFormData((prevData) => ({
        ...prevData,
        [name]: cleanValue,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFileClick = () => {
    fileRef.current?.click();
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        borrower_id: "",
        currency: "SAR",
        narration: "",
        amount: "",
        letter_of_acknowledgement: null,
      });
      
      GetHGODetails(
        (data) => {
          if (data?.id) {
            setCurrentHgoId(data.id);
          }
          if (data?.monazamUser?.company?.id) {
            setMonazamId(data.monazamUser.company.id);
          }
        },
        () => {},
        () => {}
      );
      
      GetSameMonazamHGOs(setHGOs, setLoading, () => {});
    }
  }, [isOpen]);

  const handleCreate = () => {
    setFormData({
      borrower_id: "",
      currency: "SAR",
      narration: "",
      amount: "",
      letter_of_acknowledgement: null,
    });
    SuccessToast("Loan request created successfully.");
    onClose("create");
  };

  const handleSubmit = async (e) => {
    const { borrower_id, currency, narration, amount, letter_of_acknowledgement } = formData;
    const errors = {};
    
    if (!borrower_id) errors.borrower_id = true;
    if (!amount) errors.amount = true;
    if (!narration || narration.trim() === '') errors.narration = true;
    if (!letter_of_acknowledgement) errors.letter_of_acknowledgement = true;
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      WarningToast("Please fill in all required fields.");
      return;
    }
    
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const currentTime = `${hours}:${minutes}:${seconds}`;
    
    const formDataToSend = new FormData();
    formDataToSend.append('borrower_id', borrower_id);
    formDataToSend.append('amount', amount);
    formDataToSend.append('date', currentDate);
    formDataToSend.append('time', currentTime);
    formDataToSend.append('narration', narration);
    formDataToSend.append('sender_letter_of_concern', letter_of_acknowledgement);

    setSubmitting(true);
    CreateLoanRequest(formDataToSend, (error) => {
      setSubmitting(false);
    }, () => {
      setSubmitting(false);
      handleCreate();
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal d-block modal-blur-bg">
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content merchant-modal-content">
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <h2 className="modal-heading-txt mb-0">Loan Request</h2>
            <button
              className="btn btn-success"
              onClick={() => onClose("close")}
            >
              Close <FaTimes className="me-1" />
            </button>
          </div>

          <div className="modal-body p-4">
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Request Loan From *</label>
                <select
                  name="borrower_id"
                  className={`form-control ${validationErrors.borrower_id ? 'is-invalid' : ''}`}
                  value={formData.borrower_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select</option>
                  {hgos.filter(hgo => hgo.id !== currentHgoId).map((hgo) => (
                    <option key={hgo.id} value={hgo.id}>
                      {hgo.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Currency</label>
                <input
                  type="text"
                  name="currency"
                  className="form-control"
                  value="SAR"
                  readOnly
                  disabled
                />
            </div>

            </div>

            <div className="mb-3">
              <label className="form-label">Amount *</label>
              <input
                type="text"
                name="amount"
                className={`form-control ${validationErrors.amount ? 'is-invalid' : ''}`}
                placeholder="20,555"
                value={formData.amount}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Narration *</label>
              <textarea
                name="narration"
                className={`form-control ${validationErrors.narration ? 'is-invalid' : ''}`}
                rows="3"
                placeholder="Comments"
                value={formData.narration}
                onChange={handleChange}
              />
              {validationErrors.narration && (
                <div className="invalid-feedback">
                  Narration is required.
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Letter of Acknowledgement *</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  name="letter_of_acknowledgement"
                  ref={fileRef}
                  className={`file-input ${validationErrors.letter_of_acknowledgement ? 'is-invalid' : ''}`}
                  accept=".pdf,.doc,.docx"
                  onChange={handleChange}
                />
                <label className="file-upload-label" onClick={handleFileClick}>
                  <FaUpload className="upload-icon" />
                  <span>{formData.letter_of_acknowledgement ? formData.letter_of_acknowledgement.name : 'Upload'}</span>
                </label>
                {validationErrors.letter_of_acknowledgement && (
                  <div className="invalid-feedback d-block">
                    Letter of Acknowledgement is required.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer justify-content-center border-top">
              <button
                className="btn btn-success w-50 d-flex justify-content-center align-items-center"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Submit"
                )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanRequestForm;